import { computed, ref, watch, type Ref } from 'vue';
import type { MpfFileUploadProps } from '../MpfFileUpload.props';

export interface UseMpfFileUploadEmit {
  (event: 'change', value: File[]): void;
  (event: 'invalid', message: string): void;
}

interface UseMpfFileUploadOptions {
  props: Readonly<MpfFileUploadProps>;
  emit: UseMpfFileUploadEmit;
  fileInput: Ref<HTMLInputElement | null>;
}

function getFileKey(file: File): string {
  return `${file.name}__${file.size}__${file.lastModified}`;
}

export function useMpfFileUpload({ props, emit, fileInput }: UseMpfFileUploadOptions) {
  const isDragOver = ref(false);
  const dragDepth = ref(0);

  const files = computed<File[]>({
    get: () => props.modelValue ?? [],
    set: (value) => {
      emit('change', value);
      emit('update:modelValue', value);
    },
  });

  const safeMaxFiles = computed(() => {
    const normalised = Number.isFinite(props.maxFiles) ? Number(props.maxFiles) : 10;
    return Math.max(1, Math.min(normalised, 10));
  });

  const isDisabled = computed(() => {
    return props.disabled || props.filesCount >= safeMaxFiles.value;
  });

  const maxFilesReached = computed(() => props.filesCount >= safeMaxFiles.value);

  const describedBy = computed(() => {
    return [props.hintId, props.errorId ? `${props.id}-file-error` : null]
      .filter(Boolean)
      .join(' ');
  });

  const statusText = computed(() => {
    if (!props.filesCount) {
      return props.noFileText;
    }

    if (props.filesCount === 1) {
      return '1 file chosen';
    }

    return `${props.filesCount} files chosen`;
  });

  const chooseButtonText = computed(() => {
    if (props.filesCount === 0) {
      return props.chooseFileText;
    } else if (props.filesCount > 0 && props.filesCount < props.maxFiles) {
      return props.chooseMoreFilesText;
    } else if (props.filesCount === props.maxFiles) {
      return 'Maximum files uploaded';
    }
    return props.filesCount >= props.maxFiles ? props.chooseMoreFilesText : props.chooseFileText;
  });

  const dropInstruction = computed(() => {
    if (props.filesCount === 0) {
      return props.dropInstructionText;
    }

    if (props.filesCount > 0 && props.filesCount < safeMaxFiles.value) {
      return 'or drop file';
    }

    return '';
  });
  function openFilePicker() {
    if (isDisabled.value) return;
    fileInput.value?.click();
  }

  function validateIncomingCount(filesToAdd: File[]) {
    const remaining = safeMaxFiles.value - props.filesCount;

    if (remaining <= 0) {
      emit('invalid', `You can upload a maximum of ${safeMaxFiles.value} files.`);
      return [] as File[];
    }

    if (filesToAdd.length > remaining) {
      emit('invalid', `You can only add ${remaining} more file${remaining === 1 ? '' : 's'}.`);
    }

    return filesToAdd.slice(0, remaining);
  }

  function removeDuplicates(filesToAdd: File[]) {
    const existingKeys = new Set(files.value.map(getFileKey));

    return filesToAdd.filter((file) => {
      const key = getFileKey(file);

      if (existingKeys.has(key)) {
        return false;
      }

      existingKeys.add(key);
      return true;
    });
  }

  function addFiles(incomingFiles: File[]) {
    if (!incomingFiles.length) return;

    const limitedFiles = validateIncomingCount(incomingFiles);
    const uniqueFiles = removeDuplicates(limitedFiles);

    if (!uniqueFiles.length) {
      if (incomingFiles.length) {
        emit('invalid', 'That file has already been added.');
      }
      return;
    }

    files.value = [...files.value, ...uniqueFiles];
  }

  function onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const selectedFiles = Array.from(input.files ?? []);

    addFiles(selectedFiles);

    input.value = '';
  }

  function onDragEnter() {
    if (isDisabled.value) return;
    dragDepth.value += 1;
    isDragOver.value = true;
  }

  function onDragOver() {
    if (isDisabled.value) return;
    isDragOver.value = true;
  }

  function onDragLeave() {
    if (isDisabled.value) return;

    dragDepth.value = Math.max(0, dragDepth.value - 1);

    if (dragDepth.value === 0) {
      isDragOver.value = false;
    }
  }

  function onDrop(event: DragEvent) {
    dragDepth.value = 0;
    isDragOver.value = false;

    if (isDisabled.value) return;

    const droppedFiles = Array.from(event.dataTransfer?.files ?? []);
    addFiles(droppedFiles);
  }

  watch(
    () => props.filesCount,
    () => {
      if (props.filesCount < safeMaxFiles.value) {
        return;
      }

      isDragOver.value = false;
      dragDepth.value = 0;
    },
  );

  return {
    files,
    isDragOver,
    isDisabled,
    describedBy,
    statusText,
    chooseButtonText,
    dropInstruction,
    safeMaxFiles,
    maxFilesReached,
    openFilePicker,
    onFileChange,
    onDragEnter,
    onDragOver,
    onDragLeave,
    onDrop,
  };
}
